import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { translations } from "@/i18n/translations";
import { certificatePathFor, findCertificateBySlug } from "@/data/certificateSlugs";
import { findGuideRoute, guideHref } from "@/data/guideRoutes";
import ogImage from "@/assets/intygslakaren-og-image.png";

const BASE_URL = "https://intygslakaren.se";
const MANAGED_ATTR = "data-seo-link";

type LocalizedPaths = {
  sv: string;
  en: string;
};

const STATIC_PATHS: LocalizedPaths[] = [
  { sv: "/", en: "/en" },
  { sv: "/lakarintyg", en: "/en/lakarintyg" },
  { sv: "/kontakt", en: "/en/contact" },
  { sv: "/boka-tid", en: "/en/book-appointment" },
  { sv: "/fragor-och-svar", en: "/en/faq" },
  { sv: "/infor-besoket", en: "/en/before-visit" },
  { sv: "/sa-fungerar-det", en: "/en/how-it-works" },
  { sv: "/guider", en: "/en/guides" },
  { sv: "/foretag", en: "/en/companies" },
  { sv: "/foretag/arbetsmiljo", en: "/en/companies/occupational-health" },
  { sv: "/foretag/drogtestning", en: "/en/companies/drug-testing" },
  { sv: "/foretag/tjanster", en: "/en/companies/services" },
  { sv: "/foretag/kontakt", en: "/en/companies/contact" },
  { sv: "/foretag/boka-tid", en: "/en/companies/book" },
  { sv: "/halsokontroller", en: "/en/health-checks" },
  { sv: "/om-oss", en: "/en/about-us" },
  { sv: "/integritetspolicy", en: "/en/privacy-policy" },
  { sv: "/cookie-policy", en: "/en/cookie-policy" },
  { sv: "/anvandarvillkor", en: "/en/terms-of-use" },
  { sv: "/bekraftelse", en: "/en/confirmation" },
  { sv: "/starta-arende", en: "/en/start-case" },
  { sv: "/provtagning", en: "/en/sample-collection" },
  { sv: "/drogtestning", en: "/en/drug-testing" },
  { sv: "/drogtestning/privat", en: "/en/drug-testing/private" },
  { sv: "/drogtestning/for-intyg", en: "/en/drug-testing/for-certificate" },
  { sv: "/drogtestning/forfragan", en: "/en/drug-testing/enquiry" },
];

const trimTrailingSlash = (path: string) => (path.length > 1 ? path.replace(/\/+$/, "") : path);

const getLocalizedPathsForRoute = (pathname: string): LocalizedPaths | null => {
  const normalized = trimTrailingSlash(pathname);
  if (normalized === "/läkarintyg") {
    return { sv: "/lakarintyg", en: "/en/lakarintyg" };
  }

  for (const item of STATIC_PATHS) {
    if (trimTrailingSlash(item.sv) === normalized || trimTrailingSlash(item.en) === normalized) {
      return item;
    }
  }

  const svCertMatch = normalized.match(/^\/lakarintyg\/([^/]+)$/);
  if (svCertMatch) {
    const internal = findCertificateBySlug(svCertMatch[1], "sv");
    if (internal) {
      return { sv: certificatePathFor(internal, "sv"), en: certificatePathFor(internal, "en") };
    }
  }

  const enCertMatch = normalized.match(/^\/en\/medical-certificates\/([^/]+)$/);
  if (enCertMatch) {
    const internal = findCertificateBySlug(enCertMatch[1], "en");
    if (internal) {
      return { sv: certificatePathFor(internal, "sv"), en: certificatePathFor(internal, "en") };
    }
  }

  const svGuideMatch = normalized.match(/^\/guider\/([^/]+)\/([^/]+)$/);
  if (svGuideMatch) {
    const route = findGuideRoute(svGuideMatch[1], svGuideMatch[2]);
    if (route) {
      return {
        sv: guideHref(route.category, route.slug, "sv"),
        en: guideHref(route.category, route.slug, "en"),
      };
    }
  }

  const enGuideMatch = normalized.match(/^\/en\/guides\/([^/]+)\/([^/]+)$/);
  if (enGuideMatch) {
    const route = findGuideRoute(enGuideMatch[1], enGuideMatch[2]);
    if (route) {
      return {
        sv: guideHref(route.category, route.slug, "sv"),
        en: guideHref(route.category, route.slug, "en"),
      };
    }
  }

  return null;
};

const upsertLink = (selector: string, attrs: Record<string, string>) => {
  let link = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    document.head.appendChild(link);
  }
  Object.entries(attrs).forEach(([key, value]) => link!.setAttribute(key, value));
  link.setAttribute(MANAGED_ATTR, "true");
};

const upsertMeta = (selector: string, attrs: Record<string, string>) => {
  let meta = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement("meta");
    document.head.appendChild(meta);
  }
  Object.entries(attrs).forEach(([key, value]) => meta!.setAttribute(key, value));
  meta.setAttribute(MANAGED_ATTR, "true");
};

const NOINDEX_PATHS = new Set(["/bekraftelse", "/en/confirmation"]);

const getCanonicalUrl = (pathname: string, lang: "sv" | "en") => {
  const localized = getLocalizedPathsForRoute(pathname);
  if (!localized) {
    return `${BASE_URL}${pathname === "/" ? "/" : trimTrailingSlash(pathname)}`;
  }
  const canonicalPath = lang === "sv" ? localized.sv : localized.en;
  return `${BASE_URL}${canonicalPath}`;
};

/** Public pages: index,follow. Confirmation + unknown/404: noindex,follow. */
const getRobotsContent = (pathname: string) => {
  const normalized = trimTrailingSlash(pathname);
  if (NOINDEX_PATHS.has(normalized)) return "noindex, follow";
  if (!getLocalizedPathsForRoute(pathname)) return "noindex, follow";
  return "index, follow";
};

export const SeoHeadLinks = () => {
  const { pathname } = useLocation();
  const { lang } = useLang();

  useEffect(() => {
    document.head.querySelectorAll(`link[${MANAGED_ATTR}="true"]`).forEach((node) => node.remove());

    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: getRobotsContent(pathname),
    });
    upsertMeta('meta[name="googlebot"]', {
      name: "googlebot",
      content: getRobotsContent(pathname),
    });

    const localized = getLocalizedPathsForRoute(pathname);
    if (!localized) {
      upsertLink('link[rel="canonical"]', { rel: "canonical", href: getCanonicalUrl(pathname, lang) });
      return;
    }

    const canonicalPath = lang === "sv" ? localized.sv : localized.en;
    upsertLink('link[rel="canonical"]', { rel: "canonical", href: `${BASE_URL}${canonicalPath}` });
    upsertLink('link[rel="alternate"][hreflang="sv"]', {
      rel: "alternate",
      hreflang: "sv",
      href: `${BASE_URL}${localized.sv}`,
    });
    upsertLink('link[rel="alternate"][hreflang="en"]', {
      rel: "alternate",
      hreflang: "en",
      href: `${BASE_URL}${localized.en}`,
    });
    upsertLink('link[rel="alternate"][hreflang="x-default"]', {
      rel: "alternate",
      hreflang: "x-default",
      href: `${BASE_URL}${localized.sv}`,
    });
  }, [lang, pathname]);

  useEffect(() => {
    const syncOgMeta = () => {
      const home = translations[lang].hero;
      const isHome = trimTrailingSlash(pathname) === "/" || trimTrailingSlash(pathname) === "/en";
      const title = isHome ? home.metaTitle : document.title || home.metaTitle;
      const description = isHome
        ? home.metaDescription
        : document.querySelector('meta[name="description"]')?.getAttribute("content") || home.metaDescription;

      // Keep title/description in sync for the homepage (Index may race with this effect).
      if (isHome) {
        document.title = title.slice(0, 60);
        upsertMeta('meta[name="description"]', { name: "description", content: description.slice(0, 160) });
      }

      const url = getCanonicalUrl(pathname, lang);
      const imageUrl = `${BASE_URL}${ogImage}`;
      const locale = lang === "sv" ? "sv_SE" : "en_GB";
      const alternateLocale = lang === "sv" ? "en_GB" : "sv_SE";

      upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
      upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Intygsläkaren" });
      upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
      upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
      upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
      upsertMeta('meta[property="og:image"]', { property: "og:image", content: imageUrl });
      upsertMeta('meta[property="og:image:secure_url"]', { property: "og:image:secure_url", content: imageUrl });
      upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: locale });
      upsertMeta('meta[property="og:locale:alternate"]', {
        property: "og:locale:alternate",
        content: alternateLocale,
      });

      upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
      upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
      upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
      upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: imageUrl });
    };

    // Double rAF so page-level title/description effects run first on non-home routes.
    let innerRaf = 0;
    const outerRaf = window.requestAnimationFrame(() => {
      innerRaf = window.requestAnimationFrame(syncOgMeta);
    });
    return () => {
      window.cancelAnimationFrame(outerRaf);
      window.cancelAnimationFrame(innerRaf);
    };
  }, [lang, pathname]);

  return null;
};
